import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Dumbbell, ShieldCheck, TriangleAlert } from 'lucide-react';
import { supabase } from '../supabaseClient';
import { useTheme } from '../contexts/ThemeContext';
import { useAuth } from '../contexts/AuthContext';

export const OAUTH_CONSENT_PATH = '/oauth/consent';

export function isOAuthConsentPath(pathname: string): boolean {
  return pathname.replace(/\/+$/, '') === OAUTH_CONSENT_PATH;
}

// Campos que usa la pantalla. `redirect_uri` no figura en los tipos de
// auth-js 2.89.0, pero la API de Supabase Auth lo puede incluir; se lee de
// forma defensiva y solo se usa para mostrar el host.
interface AuthorizationDetails {
  redirect_url?: string;
  redirect_uri?: string;
  client?: { name?: string };
}

type Status =
  | { kind: 'loading' }
  | { kind: 'ready'; details: AuthorizationDetails }
  | { kind: 'invalid' }
  | { kind: 'error'; message: string }
  | { kind: 'redirecting' };

// Solo se navega a URLs devueltas por Supabase, nunca a parámetros de la query.
// Se descartan esquemas peligrosos por si la respuesta viniera malformada; se
// admiten esquemas propios porque algunos clientes nativos los registran.
const BLOCKED_SCHEMES = ['javascript', 'data', 'vbscript', 'file', 'blob'];

function toSafeRedirect(url: unknown): string | null {
  if (typeof url !== 'string' || !url) return null;
  try {
    const parsed = new URL(url);
    const scheme = parsed.protocol.replace(/:$/, '').toLowerCase();
    return BLOCKED_SCHEMES.includes(scheme) ? null : url;
  } catch {
    return null;
  }
}

function hostOf(url: unknown): string | null {
  if (typeof url !== 'string' || !url) return null;
  try {
    return new URL(url).host || null;
  } catch {
    return null;
  }
}

function isInvalidAuthorization(error: { name?: string; status?: number }): boolean {
  if (error.name === 'AuthSessionMissingError') return false;
  return error.status === 400 || error.status === 403 || error.status === 404 || error.status === 410;
}

// auth-js interpola el id en la ruta de la API sin codificarlo: solo se aceptan
// caracteres seguros para que un id armado no apunte a otro endpoint.
const AUTHORIZATION_ID = /^[A-Za-z0-9_-]{1,128}$/;

function readAuthorizationId(): string | null {
  const id = new URLSearchParams(window.location.search).get('authorization_id')?.trim();
  return id && AUTHORIZATION_ID.test(id) ? id : null;
}

interface OAuthConsentProps {
  /** Navegación del navegador; inyectable para tests. */
  navigate?: (url: string) => void;
}

const defaultNavigate = (url: string) => window.location.assign(url);

export function OAuthConsent({ navigate = defaultNavigate }: OAuthConsentProps) {
  const { theme } = useTheme();
  const { user } = useAuth();
  const [authorizationId] = useState(readAuthorizationId);
  const [status, setStatus] = useState<Status>(() =>
    authorizationId ? { kind: 'loading' } : { kind: 'invalid' }
  );
  const [submitting, setSubmitting] = useState<'approve' | 'deny' | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);
  const navigateRef = useRef(navigate);
  navigateRef.current = navigate;

  useEffect(() => {
    if (!authorizationId) return;
    let cancelled = false;
    setStatus({ kind: 'loading' });

    supabase.auth.oauth
      .getAuthorizationDetails(authorizationId)
      .then(({ data, error }) => {
        if (cancelled) return;
        if (error || !data) {
          setStatus(
            error && isInvalidAuthorization(error)
              ? { kind: 'invalid' }
              : { kind: 'error', message: 'No pudimos cargar la autorización.' }
          );
          return;
        }
        const details = data as AuthorizationDetails;
        // Consentimiento previo: Supabase ya devuelve a dónde volver.
        if (details.redirect_url) {
          const target = toSafeRedirect(details.redirect_url);
          if (!target) {
            setStatus({ kind: 'error', message: 'La respuesta de autorización no es válida.' });
            return;
          }
          setStatus({ kind: 'redirecting' });
          navigateRef.current(target);
          return;
        }
        setStatus({ kind: 'ready', details });
      })
      .catch(() => {
        if (!cancelled) setStatus({ kind: 'error', message: 'No pudimos cargar la autorización.' });
      });

    return () => {
      cancelled = true;
    };
  }, [authorizationId, reloadKey]);

  const decide = useCallback(
    async (decision: 'approve' | 'deny') => {
      if (!authorizationId || submitting) return;
      setSubmitting(decision);
      setActionError(null);
      try {
        const options = { skipBrowserRedirect: true };
        const { data, error } =
          decision === 'approve'
            ? await supabase.auth.oauth.approveAuthorization(authorizationId, options)
            : await supabase.auth.oauth.denyAuthorization(authorizationId, options);
        if (error) {
          if (isInvalidAuthorization(error)) {
            setStatus({ kind: 'invalid' });
          } else {
            setActionError('No pudimos registrar tu respuesta. Probá de nuevo.');
          }
          return;
        }
        const target = toSafeRedirect(data?.redirect_url);
        if (!target) {
          setActionError('La respuesta de autorización no es válida.');
          return;
        }
        setStatus({ kind: 'redirecting' });
        navigateRef.current(target);
      } catch {
        setActionError('No pudimos registrar tu respuesta. Probá de nuevo.');
      } finally {
        setSubmitting(null);
      }
    },
    [authorizationId, submitting]
  );

  const renderBody = () => {
    switch (status.kind) {
      case 'loading':
      case 'redirecting':
        return (
          <div className="flex flex-col items-center gap-4 py-6" role="status">
            <div className="w-10 h-10 border-4 border-blue-500 border-t-transparent rounded-full animate-spin" />
            <p className={theme.textSecondary}>
              {status.kind === 'loading' ? 'Cargando...' : 'Redirigiendo...'}
            </p>
          </div>
        );

      case 'invalid':
        return (
          <div className="space-y-3 text-center" role="alert">
            <TriangleAlert className="w-8 h-8 text-amber-500 mx-auto" />
            <p className="font-medium">
              {authorizationId || new URLSearchParams(window.location.search).has('authorization_id')
                ? 'La autorización venció o no es válida.'
                : 'Falta el identificador de autorización.'}
            </p>
            <p className={`text-sm ${theme.textSecondary}`}>Volvé a conectar desde tu asistente.</p>
          </div>
        );

      case 'error':
        return (
          <div className="space-y-4 text-center" role="alert">
            <TriangleAlert className="w-8 h-8 text-red-500 mx-auto" />
            <p className="font-medium">{status.message}</p>
            <button
              type="button"
              onClick={() => setReloadKey(k => k + 1)}
              className={`px-4 py-2 rounded-lg border ${theme.border} ${theme.hover} text-sm`}
            >
              Reintentar
            </button>
          </div>
        );

      case 'ready': {
        const clientName = status.details.client?.name?.trim() || 'Una aplicación';
        // El nombre lo declara el propio cliente; el host de retorno es la única
        // señal confiable, así que sin él no se permite aprobar.
        const redirectHost = hostOf(status.details.redirect_uri);
        return (
          <div className="space-y-5">
            <h2 className="text-xl font-semibold text-center">
              {clientName} quiere acceder a tu cuenta
            </h2>

            <dl className={`text-sm rounded-lg border ${theme.border} divide-y divide-stone-500/20`}>
              <div className="flex justify-between gap-3 px-3 py-2">
                <dt className={theme.textSecondary}>Cuenta</dt>
                <dd className="font-medium truncate">{user?.email ?? '—'}</dd>
              </div>
              <div className="flex justify-between gap-3 px-3 py-2">
                <dt className={theme.textSecondary}>Vuelve a</dt>
                <dd className="font-medium truncate">{redirectHost ?? '—'}</dd>
              </div>
            </dl>

            <p className={`text-xs ${theme.textSecondary}`}>
              El nombre lo informa la aplicación. Permití solo si reconocés el sitio al que vuelve.
            </p>

            <div className="flex gap-3">
              <ShieldCheck className="w-5 h-5 text-blue-500 shrink-0 mt-0.5" />
              <p className="text-sm">
                Va a poder <strong>ver, modificar y borrar</strong> todos tus datos: rutinas,
                entrenamientos, planes y ejercicios personalizados. No hay permisos parciales.
              </p>
            </div>

            {!redirectHost && (
              <div className="p-3 rounded-lg bg-red-500/20 text-red-400 text-sm" role="alert">
                No pudimos verificar a dónde vuelve esta autorización. Por seguridad no se puede aprobar.
              </div>
            )}

            {actionError && (
              <div className="p-3 rounded-lg bg-red-500/20 text-red-400 text-sm" role="alert">
                {actionError}
              </div>
            )}

            <div className="flex gap-3">
              <button
                type="button"
                onClick={() => decide('deny')}
                disabled={submitting !== null}
                className={`flex-1 py-3 rounded-lg border ${theme.border} ${theme.hover} font-medium disabled:opacity-50 disabled:cursor-not-allowed`}
              >
                Rechazar
              </button>
              <button
                type="button"
                onClick={() => decide('approve')}
                disabled={submitting !== null || !redirectHost}
                className="flex-1 py-3 rounded-lg bg-blue-600 text-white font-medium hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Permitir
              </button>
            </div>

            <div className="text-center">
              <button
                type="button"
                // Solo esta sesión: un cierre global también cerraría los demás dispositivos.
                onClick={() => supabase.auth.signOut({ scope: 'local' })}
                disabled={submitting !== null}
                className={`text-sm ${theme.textSecondary} hover:text-blue-500`}
              >
                Usar otra cuenta
              </button>
            </div>
          </div>
        );
      }
    }
  };

  return (
    <div
      className={`min-h-screen ${theme.bg} ${theme.text} flex items-center justify-center p-4`}
      style={{
        paddingTop: 'calc(env(safe-area-inset-top) + 1rem)',
        paddingBottom: 'calc(env(safe-area-inset-bottom) + 1rem)',
        paddingLeft: 'calc(env(safe-area-inset-left) + 1rem)',
        paddingRight: 'calc(env(safe-area-inset-right) + 1rem)',
      }}
    >
      <div className={`w-full max-w-md ${theme.card} rounded-xl p-6 sm:p-8 border ${theme.border}`}>
        <div className="flex items-center justify-center gap-3 mb-6">
          <Dumbbell className="w-8 h-8 text-blue-500" />
          <h1 className="text-2xl font-bold">Groso.app</h1>
        </div>
        {renderBody()}
      </div>
    </div>
  );
}
